
/**
 * HairMax Gift Card Recipient Form
 * Native Shopify Gift Card Support
 */

(() => {
  if (customElements.get('recipient-form')) return;

  class RecipientForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.form = this.closest('form');

      this.checkbox = this.querySelector(
        '.hm-gift-recipient__checkbox'
      );

      this.fields = this.querySelector(
        '.hm-gift-recipient__fields'
      );

      this.email = this.querySelector(
        '[name="properties[Recipient email]"]'
      );

      this.nameInput = this.querySelector(
        '[name="properties[Recipient name]"]'
      );

      this.message = this.querySelector(
        '[name="properties[Message]"]'
      );

      this.date = this.querySelector(
        '[name="properties[Send on]"]'
      );

      this.offset = this.querySelector(
        '.hm-gift-recipient__offset'
      );

      this.counter = this.querySelector(
        '.hm-gift-recipient__count'
      );

      this.error = this.querySelector(
        '.hm-gift-recipient__error'
      );

      if (!this.form || !this.checkbox || !this.fields) {
        console.warn(
          'HairMax: Gift recipient form must be inside the product form.'
        );
        return;
      }

      this.initialized = true;
      this.checkbox.disabled = false;

      this.updateDateLimits();
      this.updateState();
      this.updateCount();

      this.checkbox.addEventListener('change', () => {
        this.updateState();
      });

      this.message?.addEventListener('input', () => {
        this.updateCount();
        this.clearError();
      });

      [
        this.email,
        this.nameInput,
        this.date
      ].forEach((input) => {
        input?.addEventListener('input', () => {
          this.clearError();
        });
      });

      this.form.addEventListener(
        'submit',
        (event) => {
          if (!this.validate()) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        },
        true
      );
    }

    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    }

    updateDateLimits() {
      if (!this.date) return;

      const today = new Date();

      const maxDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() + 90
      );

      this.date.min = this.formatDate(today);
      this.date.max = this.formatDate(maxDate);
    }

    updateState() {
      const enabled = this.checkbox.checked;

      this.fields.hidden = !enabled;

      this.classList.toggle('is-active', enabled);

      this.checkbox.setAttribute(
        'aria-expanded',
        String(enabled)
      );

      [
        this.email,
        this.nameInput,
        this.message,
        this.date,
        this.offset
      ].forEach((input) => {
        if (input) input.disabled = !enabled;
      });

      if (this.email) {
        this.email.required = enabled;
      }

      if (this.offset && enabled) {
        this.offset.value = String(
          new Date().getTimezoneOffset()
        );
      }

      if (!enabled) {
        this.clearError();
      }
    }

    updateCount() {
      if (!this.message || !this.counter) return;

      this.counter.textContent =
        this.message.value.length;
    }

    showError(message, input) {
      if (this.error) {
        this.error.textContent = message;
        this.error.hidden = false;
      }

      if (input) {
        input.setAttribute('aria-invalid', 'true');
        input.focus();
      }
    }

    clearError() {
      if (this.error) {
        this.error.hidden = true;
        this.error.textContent = '';
      }

      [
        this.email,
        this.nameInput,
        this.message,
        this.date
      ].forEach((input) => {
        input?.removeAttribute('aria-invalid');
      });
    }

    validate() {
      this.clearError();

      if (!this.checkbox.checked) return true;

      this.updateDateLimits();

      if (!this.email?.value.trim()) {
        this.showError(
          'Please enter the recipient email address.',
          this.email
        );
        return false;
      }

      if (!this.email.validity.valid) {
        this.showError(
          'Please enter a valid email address.',
          this.email
        );
        return false;
      }

      if (this.nameInput?.value.length > 255) {
        this.showError(
          'Recipient name cannot exceed 255 characters.',
          this.nameInput
        );
        return false;
      }

      if (this.message?.value.length > 200) {
        this.showError(
          'Message cannot exceed 200 characters.',
          this.message
        );
        return false;
      }

      if (this.date?.value) {
        if (
          this.date.value < this.date.min ||
          this.date.value > this.date.max ||
          !this.date.validity.valid
        ) {
          this.showError(
            'Please select a delivery date within the next 90 days.',
            this.date
          );
          return false;
        }
      }

      if (this.offset) {
        this.offset.value = String(
          new Date().getTimezoneOffset()
        );
      }

      return true;
    }
  }

  customElements.define('recipient-form', RecipientForm);
})();
